import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Fade
} from '@mui/material'
import { ThemeProvider, createTheme, ThemeOptions } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckIcon from '@mui/icons-material/Check'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import DevicesIcon from '@mui/icons-material/Devices'
import SlotPicker from './components/SlotPicker'
import MeetingModePicker, { MeetingModePickerHandle } from './components/MeetingModePicker'
import Confirmation from './components/Confirmation'
import BookingIntro from './components/BookingIntro'
import BookingHeader from './components/BookingHeader'
import { useBookingSession } from './useBookingSession'
import { bookingToken } from './bookingToken'
import { isFetchBaseQueryError } from '../../../api/ServiceHelper'
import rgfoLogo from '../../../assets/images/RockefellerGlobalFamilyOffice.png'

const STEPS = ['Pick a Time', 'Meeting Type', 'Confirm']

// Rockefeller Global Family Office branded theme for the public booking page.
const bookingTheme = createTheme({
  palette: {
    primary: {
      main: '#002A3A',
      light: '#013a50',
      dark: '#001a24'
    },
    secondary: {
      main: '#B49D77',
      light: '#c9b697',
      dark: '#95805c'
    },
    special: {
      main: '#007680',
      light: 'rgba(0,118,128,0.09)',
      dark: '#005c64'
    },
    info: {
      main: '#007680',
      light: 'rgba(0,118,128,0.09)',
      dark: '#005c64'
    },
    background: {
      default: '#F3F1EC',
      paper: '#FBFAF7'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
  } as ThemeOptions['typography']
})

// Rounded card that frames each booking screen.
const cardSx = {
  overflow: 'hidden',
  borderRadius: '18px',
  bgcolor: '#FBFAF7',
  border: '1px solid #E7E4DB',
  boxShadow: '0 24px 60px -30px rgba(0,42,58,0.35)'
} as const

interface Slot {
  date: string
  startTime: string
  endTime: string
}

interface CurrentBooking {
  date: string
  startTime: string
  meetingMode?: string
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (isFetchBaseQueryError(error)) {
    // Prefer the server's friendly `message` (e.g. the 429 "Too many attempts…") over the `error` code.
    const data = error.data as { message?: string; error?: string } | undefined
    return data?.message ?? data?.error ?? fallback
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message?: string }).message ?? fallback
  }
  return fallback
}

// True when the API rejected the booking because another circle took the slot first.
const isSlotTakenError = (error: unknown): boolean =>
  isFetchBaseQueryError(error) &&
  (error.data as { error?: string } | undefined)?.error === 'SLOT_TAKEN'

const BookingPage: React.FC = () => {
  // Token was captured from the URL fragment and the URL scrubbed at app startup (see bookingToken.ts),
  // before App Insights or React could read the URL.
  const [token] = useState<string | null>(bookingToken)

  const { slotsData, loading, error: slotsError, booking, bookResult: result, bookError, book, start, started } =
    useBookingSession(token)

  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null)
  const [meetingMode, setMeetingMode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The flow is a wizard: 0 = pick a time, 1 = meeting type. Confirmation is a separate screen (`result`).
  const [wizardStep, setWizardStep] = useState<0 | 1>(0)
  const modePickerRef = useRef<MeetingModePickerHandle>(null)
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Single Back control: unwind the format picker's own sub-step first (virtual → type),
  // and only return to the time step once the picker has nothing left to undo.
  const handleBack = () => {
    if (!modePickerRef.current?.goBack()) {
      setWizardStep(0)
    }
  }

  // Clear any pending auto-advance timer on unmount to avoid a state update after unmount.
  useEffect(
    () => () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
    },
    []
  )

  const slots: Slot[] = slotsData?.slots ?? []
  const isRebook = slotsData?.isRebook ?? false
  const timezoneAbbr = slotsData?.timezoneAbbreviation ?? 'ET'
  const physicalLocation = slotsData?.physicalLocation ?? null
  const allowedMeetingModes = slotsData?.allowedMeetingModes ?? null
  const currentBooking: CurrentBooking | null =
    slotsData?.currentDate && slotsData?.currentTime
      ? {
          date: slotsData.currentDate,
          startTime: slotsData.currentTime,
          meetingMode: slotsData.currentMeetingMode || undefined
        }
      : null

  useEffect(() => {
    if (!token) {
      setError('No booking token found. Please use the link from your email.')
    } else if (slotsError) {
      setError(getErrorMessage(slotsError, 'Failed to load slots'))
    } else if (bookError) {
      setError(getErrorMessage(bookError, 'Booking failed'))
      // If the slot was taken by another circle, the list has been refreshed and the
      // chosen slot may no longer exist — clear the selection and send the user back to step 1.
      if (isSlotTakenError(bookError)) {
        setSelectedSlot(null)
        setMeetingMode(null)
        setWizardStep(0)
      }
    }
  }, [token, slotsError, bookError])

  const handleBook = useCallback(async () => {
    if (!selectedSlot || !meetingMode) return
    setError(null)
    await book(selectedSlot.date, selectedSlot.startTime, meetingMode)
  }, [selectedSlot, meetingMode, book])

  const formatTime = (timeStr: string) => {
    const [h, m] = timeStr.split(':')
    const hour = Number.parseInt(h, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    let displayHour = hour % 12
    if (displayHour === 0) displayHour = 12
    return `${displayHour}:${m} ${period} ${timezoneAbbr}`
  }

  const activeStep = result ? 3 : wizardStep
  const canContinue = wizardStep === 0 ? Boolean(selectedSlot) : Boolean(meetingMode)
  const continueLabel = wizardStep === 1 ? (isRebook ? 'Reschedule Booking' : 'Confirm Booking') : 'Continue'
  const selectedSummary = selectedSlot
    ? `${new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      })} · ${formatTime(selectedSlot.startTime)}`
    : ''

  const handleContinue = () => {
    if (!canContinue) return
    if (wizardStep === 0) {
      setWizardStep(1)
    } else {
      void handleBook()
    }
  }

  if (result) {
    return (
      <ThemeProvider theme={bookingTheme}>
        <CssBaseline />
        <Box sx={{ bgcolor: '#F3F1EC', minHeight: '100vh', py: 5, px: 2.5 }}>
          <Container maxWidth="sm" disableGutters sx={{ maxWidth: '520px !important' }}>
            <Confirmation result={result} />
            <Footer />
          </Container>
        </Box>
      </ThemeProvider>
    )
  }

  // Intro/consent gate — session exchange + slot load don't start until the user clicks Continue.
  if (!started) {
    return (
      <ThemeProvider theme={bookingTheme}>
        <CssBaseline />
        <Box sx={{ bgcolor: '#F3F1EC', minHeight: '100vh', py: 5, px: 2.5 }}>
          <Container maxWidth="sm" disableGutters sx={{ maxWidth: '520px !important' }}>
            <BookingIntro onStart={start} hasToken={Boolean(token)} />
            <Footer />
          </Container>
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={bookingTheme}>
      <CssBaseline />
      <Box sx={{ bgcolor: '#F3F1EC', minHeight: '100vh', py: 5, px: 2.5 }}>
        <Container maxWidth="sm" disableGutters sx={{ maxWidth: '520px !important' }}>
          <Paper elevation={0} sx={cardSx}>
            {/* Header */}
            <BookingHeader
              title={isRebook ? 'Reschedule Meeting' : 'Book Your Meeting'}
              subtitle={
                isRebook
                  ? 'Pick a new time for your advisory meeting below.'
                  : 'Choose a convenient time and format for your advisory meeting.'
              }
            />

            {/* Stepper */}
            <Box sx={{ bgcolor: '#F0EEE7', borderBottom: '1px solid #E7E4DB', px: { xs: 2, sm: 3 }, py: 2.5 }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  '& .MuiStepConnector-line': { borderColor: '#D9D9D9', borderTopWidth: 2 },
                  '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: '#007680' },
                  '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#007680' },
                  '& .MuiStepIcon-root': { color: '#CDD3D3', fontSize: '1.9rem' },
                  '& .MuiStepIcon-root.Mui-active': { color: '#007680' },
                  '& .MuiStepIcon-root.Mui-completed': { color: '#007680' },
                  '& .MuiStepLabel-label': { fontSize: '0.78rem', mt: 1, fontWeight: 400, color: '#93999A' },
                  '& .MuiStepLabel-label.Mui-active': { color: '#002A3A', fontWeight: 600 },
                  '& .MuiStepLabel-label.Mui-completed': { color: '#002A3A', fontWeight: 600 }
                }}
              >
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Body */}
            <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 3.5, minHeight: 220 }}>
              {/* Rebook alert (shown on the time step) */}
              {wizardStep === 0 && isRebook && currentBooking && (
                <Alert
                  severity="warning"
                  sx={{
                    mb: 3,
                    borderRadius: '11px',
                    bgcolor: '#FBF3E6',
                    color: '#6B4E1E',
                    border: '1px solid #E9D8B4',
                    '& .MuiAlert-icon': { color: '#B49D77' }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    A meeting is already booked
                  </Typography>
                  <Typography variant="body2">
                    Scheduled for{' '}
                    <strong>
                      {new Date(currentBooking.date + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </strong>{' '}
                    at <strong>{formatTime(currentBooking.startTime)}</strong>
                    {currentBooking.meetingMode && <> ({currentBooking.meetingMode})</>}.
                  </Typography>
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '11px' }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress size={36} sx={{ color: '#007680' }} />
                  <Typography variant="body2" sx={{ mt: 2, color: '#5A6567' }}>
                    Loading available times…
                  </Typography>
                </Box>
              )}

              {/* Step 1 — Pick a time */}
              {!loading && !error && wizardStep === 0 && (
                <Fade in timeout={350}>
                  <Box>
                    <Box
                      sx={{
                        bgcolor: '#F4F1E8',
                        border: '1px solid #EAE5D6',
                        borderRadius: '9px',
                        px: 1.75,
                        py: 1.25,
                        mb: 3
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 300, color: '#8A8577', lineHeight: 1.5 }}>
                        Please complete your booking in this session and avoid refreshing the page.
                      </Typography>
                    </Box>
                    <SectionLabel
                      icon={<CalendarMonthIcon />}
                      text={`Select a Date & Time (${timezoneAbbr})`}
                      active
                    />
                    <SlotPicker
                      slots={slots}
                      selectedSlot={selectedSlot}
                      currentBooking={currentBooking}
                      onSelect={(slot) => {
                        setSelectedSlot(slot)
                        setMeetingMode(null)
                        if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
                        advanceTimerRef.current = setTimeout(() => setWizardStep(1), 250)
                      }}
                      disabled={booking}
                      timezoneAbbr={timezoneAbbr}
                    />
                  </Box>
                </Fade>
              )}

              {/* Step 2 — Meeting format */}
              {!loading && !error && wizardStep === 1 && (
                <Fade in timeout={350}>
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: 'rgba(0,118,128,0.09)',
                        border: '1px solid rgba(0,118,128,0.25)',
                        borderRadius: '11px',
                        px: 1.9,
                        py: 1.6,
                        mb: 3
                      }}
                    >
                      <CheckIcon sx={{ color: '#007680', fontSize: 22, flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#002A3A' }}>
                          Time selected
                        </Typography>
                        <Typography sx={{ fontWeight: 300, fontSize: '0.82rem', color: '#5A6567', mt: 0.25 }}>
                          {selectedSummary}
                        </Typography>
                      </Box>
                    </Box>
                    <SectionLabel icon={<DevicesIcon />} text="Choose Meeting Format" active />
                    <MeetingModePicker
                      ref={modePickerRef}
                      selectedMode={meetingMode}
                      onSelect={setMeetingMode}
                      physicalLocation={physicalLocation}
                      allowedMeetingModes={allowedMeetingModes}
                    />
                  </Box>
                </Fade>
              )}
            </Box>

            {/* Footer nav — Back / Continue */}
            {!loading && !error && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  px: { xs: 2.5, sm: 4 },
                  py: 2.5,
                  borderTop: '1px solid #ECE9E0',
                  bgcolor: '#FBFAF7'
                }}
              >
                {wizardStep === 1 && (
                  <Button
                    onClick={handleBack}
                    disabled={booking}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      py: 1.5,
                      px: 2.75,
                      borderRadius: '11px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: '#002A3A',
                      border: '1px solid #D8D3C6',
                      bgcolor: 'transparent',
                      '&:hover': { bgcolor: '#F0EEE7' }
                    }}
                  >
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue || booking}
                  endIcon={!booking && <ArrowForwardIcon />}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: '11px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    bgcolor: '#002A3A',
                    color: '#F3F1EC',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#013a50', boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: '#002A3A', color: '#F3F1EC', opacity: 0.35 }
                  }}
                >
                  {booking ? <CircularProgress size={22} color="inherit" /> : continueLabel}
                </Button>
              </Box>
            )}
          </Paper>

          <Footer />
        </Container>
      </Box>
    </ThemeProvider>
  )
}

function SectionLabel({ icon, text, active }: Readonly<{ icon: React.ReactNode; text: string; active: boolean }>) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
      <Box sx={{ color: active ? '#007680' : '#B7BDBD', display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ color: active ? '#002A3A' : '#5A6567', fontWeight: 600, fontSize: '1.05rem' }}>
        {text}
      </Typography>
    </Box>
  )
}

function Footer() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3.5 }}>
      <Box
        component="img"
        src={rgfoLogo}
        alt="Rockefeller Global Family Office"
        sx={{ display: 'block', width: '100%', maxWidth: 210, height: 'auto' }}
      />
    </Box>
  )
}

export default BookingPage
